import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FormControlProps } from '@mui/material/FormControl';
import { InputBaseProps } from '@mui/material/InputBase';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { SxProps } from '@mui/system';
import { get, isString } from 'lodash-es';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  InputProps?: Partial<OutlinedInputProps>;
  control: Control<TFieldValues>;
  dataUi?: string;
  defaultValue?: string | number;
  disabled?: boolean;
  errors?: FieldErrors<TFieldValues>;
  id?: string;
  inputProps?: InputBaseProps['inputProps'];
  isInline?: boolean;
  isRequired?: boolean;
  label?: string;
  labelColor?: string;
  loading?: boolean;
  margin?: FormControlProps['margin'];
  multiline?: boolean;
  name: string;
  onBlur?: InputBaseProps['onBlur'];
  onFocus?: InputBaseProps['onFocus'];
  placeholder?: string;
  rows?: number;
  showErrorMessage?: boolean;
  sx?: SxProps<Theme>;
  type?: string;
}

const TextFormField = <TFieldValues extends FieldValues = FieldValues>({
  control,
  errors,
  name,
  label,
  type = 'text',
  disabled = false,
  isRequired,
  sx,
  placeholder,
  id,
  inputProps,
  InputProps: InputPropsProp,
  multiline = false,
  rows,
  showErrorMessage = false,
  loading = false,
  isInline = false,
  labelColor = 'text.primary',
  margin = 'dense',
  onFocus,
  onBlur,
  defaultValue,
  dataUi,
}: FormFieldProps<TFieldValues>) => {
  const hasAsyncError = get(errors, name)?.type === 'manual';
  const errorMessage = get(errors, `${name}.message`);

  return (
    <Controller
      name={name as FieldPath<TFieldValues>}
      control={control}
      render={({ field: { onChange, value, ...rest } }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isInline ? 'row' : 'column',
            alignItems: isInline ? 'center' : 'flex-start',
            width: '100%',
            ...sx,
          }}
        >
          {label && (
            <FormFieldLabel
              label={`${label}${isRequired ? ' *' : ''}`}
              labelColor={labelColor}
              sx={{ mr: isInline ? 1 : '' }}
            ></FormFieldLabel>
          )}
          <TextField
            onChange={onChange}
            {...rest}
            onWheel={(e) => e.target instanceof HTMLElement && e?.target?.blur?.()}
            value={value ?? defaultValue ?? ''}
            size={'small'}
            rows={rows || 1}
            multiline={multiline}
            fullWidth
            id={id || name}
            margin={margin}
            type={type}
            variant="outlined"
            disabled={disabled}
            placeholder={placeholder}
            error={Boolean(get(errors, name))}
            helperText={
              (hasAsyncError || showErrorMessage) && isString(errorMessage)
                ? errorMessage
                : ''
            }
            onFocus={onFocus}
            onBlur={onBlur}
            className={isRequired && !disabled ? 'required' : ''}
            slotProps={{
              htmlInput: {
                ...inputProps,
                ...(dataUi && { 'data-ui': dataUi }),
              },
              input: {
                ...InputPropsProp,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {InputPropsProp?.endAdornment}
                  </>
                ),
              },
            }}
          />
        </Box>
      )}
    />
  );
};

export { TextFormField };
